import { campiModificati } from "@/components/Conflitti";
import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import AnagraficaPicker from "@/components/AnagraficaPicker";
import CurrencyInput, { formatCurrencyIT } from "@/components/CurrencyInput";
import {
  Calculator, RotateCcw, Info, Pencil, Trash2, Plus, Eye, ChevronDown, ChevronRight,
} from "lucide-react";
import CodiceIvaPicker from "@/components/CodiceIvaPicker";
import { toast } from "sonner";
import {
  THEAD_WRAPPER, COL_HEADER_LEFT, COL_HEADER_RIGHT_AMBER, COL_HEADER_RIGHT_BLUE,
  CELL_TEXT_DEFAULT, VALORE_TESTO_DEFAULT,
  CELL_MONEY_ACQUISTO, CELL_MONEY_VENDITA,
  CELL_AZIONI, ICON_BTN_EDIT, ICON_BTN_DELETE,
} from "@/lib/cardStyles";

const formatEUR = (n) =>
  n === null || n === undefined || n === "" ? "—" : `${formatCurrencyIT(n, false)} €`;
// Sprint FIX-MONITOR2 — resa UNIFORME del valore acquisto mascherato: stringa vuota
const formatEURMasked = (n) =>
  n === null || n === undefined || n === "" ? "" : `${formatCurrencyIT(n, false)} €`;

const TIPI_LABEL = {
  voltura: "Voltura",
  minivoltura: "MiniVoltura",
  immatricolazione: "Immatricolazione",
};

const CATEGORIA_LABEL = {
  autoveicoli: "Autoveicoli",
  motoveicoli: "Motoveicoli",
  veicoli_commerciali: "Veicoli commerciali",
  auto_storica: "Auto storica",
  roulotte_camper: "Roulotte/Camper",
  altro: "Altro",
};

function emptyDettaglio() {
  return {
    emolumenti_aci: 0, imposta_bollo_pra: 0, diritti_dt: 0,
    imposta_bollo_carta_circolazione: 0, recupero_commissioni: 0,
    costo_marca_bollo: 0, costo_incasso_pra: 0,
  };
}

// Sprint 4I1.B3.1 — Mappa codici IVA → aliquota % (sincronizzata con master_data_codici_iva)
const IVA_ALIQUOTA = {
  I22: 22, I10: 10, I4: 4,
  E10: 0, N1: 0, N2: 0, N3: 0, N4: 0, N5: 0, N6: 0, N7: 0,
};
function ivaPercentFromCodice(codice) {
  if (!codice) return 0;
  return IVA_ALIQUOTA[codice] ?? 0;
}


function formatDateIT(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT");
  } catch {
    return iso;
  }
}

// =============== VIEW (Sprint 4F2.4 — TABLE pixel-pari con Garanzie) ===============
// Stessa struttura <table>/<thead>/<tbody> e stessi className via cardStyles.
export function PassaggioProprietaView({
  vendita, agenziaName, canEdit, onEdit, onDelete,
}) {
  const [ivaInfoOpen, setIvaInfoOpen] = React.useState(false);
  const p = vendita?.passaggio_proprieta;
  if (!p) return <div className="text-sm text-slate-400 italic py-2 mt-2">Nessun passaggio di proprietà</div>;
  // Sprint FIX-MONITOR2 — preserva null in monitor cliente: NON forzare a 0.
  const tot_acq = p.totale_acquisto_calcolato == null ? null : Number(p.totale_acquisto_calcolato);
  const pv = Number(p.prezzo_vendita_cliente || 0);
  const hasFlag = p?._data_quality?.iva_da_codificare;
  return (
    <>
    <table className="w-full text-sm mt-2" data-testid="passaggio-view">
      <thead className={THEAD_WRAPPER}>
        <tr>
          <th className={COL_HEADER_LEFT}>Agenzia</th>
          <th className={`${COL_HEADER_LEFT} w-[110px]`}>Data atto</th>
          <th className={`${COL_HEADER_RIGHT_AMBER} w-[120px]`}>Acquisto</th>
          <th className={`${COL_HEADER_RIGHT_BLUE} w-[120px]`}>Vendita</th>
          <th className="w-[70px] text-right pr-2">
            <button
              type="button"
              onClick={() => setIvaInfoOpen(true)}
              className="text-slate-400 hover:text-slate-700 inline-flex items-center"
              title="Visualizza dettaglio Passaggio"
              data-testid="btn-pass-iva-view">
              <Eye className="h-4 w-4" />
              {hasFlag && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-amber-500" title="IVA da codificare" />}
            </button>
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        <tr className="hover:bg-slate-50" data-testid="passaggio-row">
          <td className={CELL_TEXT_DEFAULT}>
            <span className={VALORE_TESTO_DEFAULT}>
              {agenziaName || <span className="text-slate-400">—</span>}
            </span>
          </td>
          <td className="py-1.5 text-[12px] text-slate-700">{formatDateIT(p.data_atto)}</td>
          <td className={CELL_MONEY_ACQUISTO} data-testid="pass-acquisto">
            {formatEURMasked(tot_acq)}
          </td>
          <td className={CELL_MONEY_VENDITA} data-testid="pass-vendita">
            {formatEUR(pv)}
          </td>
          <td className={CELL_AZIONI}>
            {canEdit && (
              <>
                <button onClick={onEdit}
                        className={ICON_BTN_EDIT}
                        title="Modifica"
                        data-testid="btn-pass-popup">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={onDelete}
                        className={ICON_BTN_DELETE}
                        title="Elimina"
                        data-testid="btn-pass-delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          </td>
        </tr>
      </tbody>
    </table>
    <PassaggioIvaInfoPopup
      open={ivaInfoOpen}
      onOpenChange={setIvaInfoOpen}
      passaggio={p}
      agenziaName={agenziaName}
    />
    </>
  );
}

// =============== POPUP (Sprint 4F2.2 — layout aggiornato) ===============
export default function PassaggioProprietaPopup({
  open, onOpenChange, vendita, onSaved, partenza,
}) {
  const initial = vendita?.passaggio_proprieta;
  const [form, setForm] = useState({});
  const [loadingSugg, setLoadingSugg] = useState(false);
  const [hasImported, setHasImported] = useState(false);
  const [originalImport, setOriginalImport] = useState(null);
  const [saving, setSaving] = useState(false);
  // Sprint 4I1.B3.1.1 — IVA default azienda + lookup agenzia
  const [aziendaIvaDefault, setAziendaIvaDefault] = useState(null);
  const [agenziaInfo, setAgenziaInfo] = useState(null); // { id, name, iva_codice }

  // Fetch IVA default azienda al mount (solo per nuovo passaggio)
  useEffect(() => {
    if (!open || !vendita?.company_id) return;
    let cancelled = false;
    api.get(`/companies/${vendita.company_id}`)
      .then((r) => {
        if (cancelled) return;
        setAziendaIvaDefault(r.data?.iva_codice_default || "I22");
      })
      .catch(() => { if (!cancelled) setAziendaIvaDefault("I22"); });
    return () => { cancelled = true; };
  }, [open, vendita?.company_id]);

  // Fetch agenzia su cambio agenzia_disbrigo_id (per origin label live)
  useEffect(() => {
    if (!open) return;
    const aid = form?.agenzia_disbrigo_id;
    if (!aid) { setAgenziaInfo(null); return; }
    let cancelled = false;
    api.get(`/anagrafiche/${aid}`)
      .then((r) => {
        if (cancelled) return;
        const a = r.data || {};
        const nome = a.ragione_sociale || `${a.nome || ""} ${a.cognome || ""}`.trim();
        setAgenziaInfo({ id: aid, name: nome, iva_codice: a.iva_codice || null });
        // Se l'utente NON ha override esplicito, aggiorna IVA da agenzia
        if (a.iva_codice && !form?.competenze_agenzia_iva_codice_override) {
          setForm((f) => ({
            ...f,
            competenze_agenzia_iva_codice: a.iva_codice,
            competenze_agenzia_iva_codice_origin: "fornitore_default",
          }));
        }
      })
      .catch(() => { if (!cancelled) setAgenziaInfo(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form?.agenzia_disbrigo_id]);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        ...initial,
        costi_fissi_dettaglio: initial.costi_fissi_dettaglio || emptyDettaglio(),
      });
      setHasImported(false);
      setOriginalImport(null);
    } else {
      // Sprint 4F2.2 B4 / 4I1.B3.1.1: data_atto vuota + IVA pre-fill da azienda
      setForm({
        tipo: "voltura",
        data_atto: "",
        agenzia_disbrigo_id: null,
        costi_fissi_modalita: "totale_unico",
        costi_fissi_dettaglio: emptyDettaglio(),
        costi_fissi_totale: 0,
        competenze_agenzia_imponibile: 0,
        competenze_agenzia_iva_codice: aziendaIvaDefault || "I22",
        competenze_agenzia_iva_codice_origin: "default_azienda",
        ricarico_imponibile: 0,
        ricarico_iva_codice: aziendaIvaDefault || "I22",
        targhe: 0, spese_spedizione: 0,
        ipt_importo: 0,
        // Sprint 4F2.2 B7: prezzo personalizzato VUOTO (non più suggerito)
        prezzo_vendita_cliente: null,
        note: "",
      });
      setHasImported(false);
      setOriginalImport(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial, aziendaIvaDefault]);

  // ----- Derivati live (anteprima nel modal) -----
  const derived = useMemo(() => {
    const f = form || {};
    const modalita = f.costi_fissi_modalita || "totale_unico";
    let cf_tot = 0;
    if (modalita === "dettaglio") {
      const det = f.costi_fissi_dettaglio || {};
      cf_tot = Number(det.emolumenti_aci || 0)
        + Number(det.imposta_bollo_pra || 0)
        + Number(det.diritti_dt || 0)
        + Number(det.imposta_bollo_carta_circolazione || 0)
        + Number(det.recupero_commissioni || 0)
        + Number(det.costo_marca_bollo || 0)
        + Number(det.costo_incasso_pra || 0);
    } else {
      cf_tot = Number(f.costi_fissi_totale || 0);
    }
    const ca_imp = Number(f.competenze_agenzia_imponibile || 0);
    // Sprint 4I1.B3.1 — aliquota IVA derivata da iva_codice (lookup client-side)
    const ca_iva_p = ivaPercentFromCodice(f.competenze_agenzia_iva_codice);
    const ca_iva_calc = ca_imp * ca_iva_p / 100;
    const ca_tot = ca_imp + ca_iva_calc;
    const ric_imp = Number(f.ricarico_imponibile || 0);
    const ric_iva_p = ivaPercentFromCodice(f.ricarico_iva_codice || "I22");
    const ric_iva_calc = ric_imp * ric_iva_p / 100;
    const ric_tot = ric_imp + ric_iva_calc;
    const ipt = Number(f.ipt_importo || 0);
    const targhe = Number(f.targhe || 0);
    const spese = Number(f.spese_spedizione || 0);
    const tot_acquisto = ipt + cf_tot + ca_tot + targhe + spese;
    const sug = tot_acquisto + ric_tot;
    const pv = Number(f.prezzo_vendita_cliente || 0);
    const margine = pv - tot_acquisto;
    return {
      cf_tot, ca_iva_calc, ca_tot, ric_iva_calc, ric_tot,
      tot_acquisto, sug, margine,
    };
  }, [form]);

  // Sprint 4F2.4 C1: prefetch kW + Provincia readonly per visualizzazione modal
  // (anche se l'utente non ha ancora cliccato "Calcola passaggio").
  useEffect(() => {
    if (!open || !vendita?.id) return;
    if (form.kw_veicolo_snapshot && form.provincia_snapshot) return;
    // Strategia: prova suggested-config (caso happy); fallback su vehicle/cliente direct.
    (async () => {
      try {
        const r = await api.get(`/vendite/${vendita.id}/passaggio/suggested-config`);
        const d = r.data;
        setForm((f) => ({
          ...f,
          kw_veicolo_snapshot: f.kw_veicolo_snapshot ?? d.kw_veicolo_snapshot,
          provincia_snapshot: f.provincia_snapshot ?? d.provincia_snapshot,
          provincia_source: f.provincia_source ?? d.provincia_source,
          tipo_origine_snapshot: f.tipo_origine_snapshot ?? d.tipo_origine_snapshot,
          categoria_veicolo_snapshot: f.categoria_veicolo_snapshot ?? d.categoria_veicolo_snapshot,
        }));
      } catch (_) {
        // Fallback diretto: GET vehicle + cliente
        try {
          const rv = await api.get(`/vehicles/${vendita.vehicle_id}`);
          const v = rv.data;
          let prov = null; let provSource = null;
          if (vendita.cliente_id) {
            try {
              const rc = await api.get(`/anagrafiche/${vendita.cliente_id}`);
              const a = rc.data;
              prov = a?.residenza?.provincia || a?.indirizzo?.provincia
                || a?.sede_legale?.provincia || a?.provincia || null;
              if (prov) provSource = "cliente";
            } catch (_e) { /* ignore */ }
          }
          if (!prov && vendita.company_id) {
            try {
              const rk = await api.get(`/companies/${vendita.company_id}`);
              prov = rk.data?.sede_legale?.provincia || rk.data?.provincia || null;
              provSource = "azienda";
            } catch (_e) { /* ignore */ }
          }
          setForm((f) => ({
            ...f,
            kw_veicolo_snapshot: f.kw_veicolo_snapshot ?? v?.kw ?? null,
            provincia_snapshot: f.provincia_snapshot ?? prov,
            provincia_source: f.provincia_source ?? provSource,
            categoria_veicolo_snapshot: f.categoria_veicolo_snapshot ?? v?.categoria_veicolo ?? null,
            tipo_origine_snapshot: f.tipo_origine_snapshot
              ?? (v?.provenienza === "importazione" ? "importazione" : "nazionale"),
          }));
        } catch (_e2) { /* lascia "—" */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vendita?.id]);

  const calcolaPassaggio = async () => {
    setLoadingSugg(true);
    try {
      const r = await api.get(`/vendite/${vendita.id}/passaggio/suggested-config`);
      const d = r.data;
      const next = {
        ...form,
        // Tipo lo lasciamo se l'utente l'ha già cambiato; in nuovo lo settiamo
        tipo: form.tipo || d.tipo,
        kw_veicolo_snapshot: d.kw_veicolo_snapshot,
        provincia_snapshot: d.provincia_snapshot,
        provincia_source: d.provincia_source,
        tipo_origine_snapshot: d.tipo_origine_snapshot,
        categoria_veicolo_snapshot: d.categoria_veicolo_snapshot,
        ipt_importo: d.ipt_importo,
        ipt_modalita_snapshot: d.ipt_modalita_snapshot,
        costi_fissi_modalita: d.costi_fissi_modalita,
        costi_fissi_dettaglio: d.costi_fissi_dettaglio || emptyDettaglio(),
        costi_fissi_totale: d.costi_fissi_totale ?? 0,
        competenze_agenzia_imponibile: d.competenze_agenzia_imponibile,
        competenze_agenzia_iva_percent: d.competenze_agenzia_iva_percent,
        ricarico_imponibile: d.ricarico_imponibile,
        ricarico_iva_percent: d.ricarico_iva_percent,
        targhe: d.targhe,
        spese_spedizione: d.spese_spedizione,
        // Sprint 4F2.2 B7: prezzo cliente NON precompilato (resta vuoto)
      };
      setForm(next);
      setOriginalImport(next);
      setHasImported(true);
      toast.success("Passaggio calcolato dalla configurazione azienda");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore calcolo passaggio";
      toast.error(msg);
    } finally {
      setLoadingSugg(false);
    }
  };

  const resetCalcolo = () => {
    if (!originalImport) return;
    setForm((f) => ({
      ...originalImport,
      // Preserva input utente "manuali"
      agenzia_disbrigo_id: f.agenzia_disbrigo_id,
      data_atto: f.data_atto,
      tipo: f.tipo,
      note: f.note,
      prezzo_vendita_cliente: f.prezzo_vendita_cliente,
    }));
    toast.success("Valori reimpostati ai default del calcolo");
  };

  const handleSave = async () => {
    // Sprint 4F2.2 B7: prezzo cliente obbligatorio
    const pv = Number(form.prezzo_vendita_cliente);
    if (!form.prezzo_vendita_cliente || isNaN(pv) || pv <= 0) {
      // eslint-disable-next-line no-alert
      window.alert("Prezzo cliente obbligatorio. Inserisci un valore prima di salvare.");
      return;
    }
    if (!form.tipo) { toast.error("Tipo passaggio obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      // Pulisci campi UI-only o derived
      delete payload.provincia_source;
      delete payload._created_at;
      delete payload._updated_at;
      delete payload.created_by;
      delete payload.updated_by;
      ["competenze_agenzia_iva_calc", "competenze_agenzia_totale",
       "ricarico_iva_calc", "ricarico_totale", "totale_acquisto_calcolato",
       "prezzo_suggerito", "margine_passaggio", "costi_fissi_totale_calc"].forEach((k) => delete payload[k]);
      // Normalizza data_atto vuota
      if (!payload.data_atto) payload.data_atto = null;
      if (!payload.agenzia_disbrigo_id) payload.agenzia_disbrigo_id = null;
      // Sprint PRATICHE-CONCORRENZA — solo i campi cambiati rispetto all'apertura della finestra
      const { payload: diff, originali } = campiModificati(partenza || {}, payload);
      const r = await api.put(`/vendite/${vendita.id}/passaggio`, { ...diff, originali });
      if (!r.data.conflitti?.length) toast.success(initial ? "Passaggio aggiornato" : "Passaggio creato");
      onSaved?.(r.data, diff);
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore salvataggio passaggio";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[820px] max-h-[90vh] overflow-y-auto" data-testid="passaggio-popup">
        <DialogHeader>
          <DialogTitle>
            {initial ? "Modifica" : "Nuovo"} — Passaggio di proprietà
          </DialogTitle>
          <DialogDescription>
            Configura IPT, costi fissi, competenze agenzia, ricarico e prezzo cliente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">

          {/* ============ SEZIONE 1 — Anagrafiche / Tipo ============ */}
          <section>
            <h3 className="text-lg font-bold border-b border-slate-300 pb-1 mb-2.5">Anagrafiche e tipo</h3>
            <div className="space-y-3">
              {/* Categoria veicolo — 100% larghezza */}
              <div>
                <Label className="text-xs">Categoria veicolo <span className="text-slate-400 font-normal">(da scheda veicolo)</span></Label>
                <Input value={CATEGORIA_LABEL[form.categoria_veicolo_snapshot] || "—"} disabled className="bg-slate-50" />
              </div>
              {/* Sprint 4F2.4 C1 — kW + Provincia readonly (50/50) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">kW <span className="text-slate-400 font-normal">(da scheda veicolo)</span></Label>
                  <Input value={form.kw_veicolo_snapshot ?? "—"} disabled className="bg-slate-50"
                         data-testid="pass-readonly-kw" />
                </div>
                <div>
                  <Label className="text-xs">
                    Provincia
                    <span className="text-slate-400 font-normal">
                      {" "}({form.provincia_source === "azienda" ? "da azienda" : "da anagrafica cliente"})
                    </span>
                  </Label>
                  <Input value={form.provincia_snapshot || "—"} disabled className="bg-slate-50"
                         data-testid="pass-readonly-provincia" />
                </div>
              </div>
              {/* Tipo passaggio */}
              <div>
                <Label className="text-xs">Tipo passaggio</Label>
                <div className="flex gap-2 mt-1.5">
                  {Object.keys(TIPI_LABEL).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, tipo: t }))}
                      className={`px-2.5 py-1 text-xs rounded-md border ${form.tipo === t ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-300"}`}
                      data-testid={`pass-tipo-${t}`}
                    >{TIPI_LABEL[t]}</button>
                  ))}
                </div>
              </div>
              {/* Data atto */}
              <div>
                <Label className="text-xs">Data atto <span className="text-slate-400 font-normal">(opzionale, obbligatoria prima della consegna)</span></Label>
                <Input type="date" value={form.data_atto || ""}
                       onChange={(e) => setForm((f) => ({ ...f, data_atto: e.target.value }))}
                       data-testid="pass-data-atto" />
              </div>
              {/* Agenzia */}
              <div>
                <AnagraficaPicker
                  label="Agenzia disbrigo (opzionale)"
                  helper="Categoria: Fornitore servizi veicoli. Obbligatoria prima della consegna."
                  value={form.agenzia_disbrigo_id}
                  onChange={(id) => setForm((f) => ({ ...f, agenzia_disbrigo_id: id }))}
                  filterCategoria="fornitore_servizi_veicoli"
                  contextCompanyId={vendita?.company_id || null}
                  testIdPrefix="pass-agenzia"
                />
              </div>
            </div>
          </section>

          {/* ============ SEZIONE 2 (era 3) — Calcola / Reset ============ */}
          <section className="bg-slate-50 rounded-md p-3">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={calcolaPassaggio}
                disabled={loadingSugg}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="btn-calcola-passaggio"
              >
                <Calculator className="h-4 w-4 mr-1.5" />
                Calcola passaggio
              </Button>
              {hasImported && (
                <Button
                  type="button" variant="outline"
                  onClick={resetCalcolo}
                  className="border-amber-400 text-amber-700"
                  data-testid="btn-reset-calcolo"
                >
                  <RotateCcw className="h-4 w-4 mr-1.5" /> Reset calcolo passaggio
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-2 flex items-start gap-1.5">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              Recupera i valori da <strong>Settings → Passaggio di proprietà</strong> e calcola l'IPT in base ai kW del veicolo.
            </p>
          </section>

          {/* ============ SEZIONE 3 (era 4) — IPT + Altri oneri esenti IVA ============ */}
          <section>
            <h3 className="text-lg font-bold border-b border-slate-300 pb-1 mb-2.5">IPT + altri oneri (esenti IVA)</h3>
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div>
                <Label className="text-xs">IPT</Label>
                <CurrencyInput
                  value={form.ipt_importo}
                  onChange={(v) => setForm((f) => ({ ...f, ipt_importo: v }))}
                  data-testid="pass-ipt"
                />
                {form.ipt_modalita_snapshot === "automatico" && form.kw_veicolo_snapshot && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Calcolato automaticamente da kW veicolo (modalità: automatico)
                  </p>
                )}
                {form.ipt_modalita_snapshot === "manuale" && (
                  <p className="text-[11px] text-amber-600 mt-1">
                    Inserisci IPT manualmente (modalità: manuale)
                  </p>
                )}
              </div>
              <div className="flex items-end gap-2 pb-1">
                <Switch
                  checked={form.costi_fissi_modalita === "dettaglio"}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, costi_fissi_modalita: v ? "dettaglio" : "totale_unico" }))}
                  data-testid="pass-toggle-dettaglio"
                />
                <Label className="text-xs">Mostra dettaglio costi fissi</Label>
              </div>
            </div>

            {form.costi_fissi_modalita === "dettaglio" ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-2.5 rounded-md bg-slate-50">
                {[
                  ["emolumenti_aci", "Emolumenti ACI"],
                  ["imposta_bollo_pra", "Imposta bollo PRA"],
                  ["diritti_dt", "Diritti DT"],
                  ["imposta_bollo_carta_circolazione", "Bollo carta circolazione"],
                  ["recupero_commissioni", "Recupero commissioni"],
                  ["costo_marca_bollo", "Costo marca bollo"],
                  ["costo_incasso_pra", "Costo incasso PRA"],
                ].map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-[11px]">{label}</Label>
                    <CurrencyInput
                      value={form.costi_fissi_dettaglio?.[k] || 0}
                      onChange={(v) => setForm((f) => ({
                        ...f, costi_fissi_dettaglio: { ...(f.costi_fissi_dettaglio || emptyDettaglio()), [k]: v },
                      }))}
                      data-testid={`pass-cf-${k}`}
                    />
                  </div>
                ))}
                <div className="col-span-2 md:col-span-3 text-right text-xs text-slate-600 pt-1.5 border-t">
                  Totale dettaglio: <span className="font-mono font-semibold">{formatEUR(derived.cf_tot)}</span>
                </div>
              </div>
            ) : (
              <div>
                <Label className="text-xs">Totale costi fissi (modalità unica)</Label>
                <CurrencyInput
                  value={form.costi_fissi_totale}
                  onChange={(v) => setForm((f) => ({ ...f, costi_fissi_totale: v }))}
                  data-testid="pass-cf-totale"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mt-2.5">
              <div>
                <Label className="text-xs">Targhe</Label>
                <CurrencyInput
                  value={form.targhe}
                  onChange={(v) => setForm((f) => ({ ...f, targhe: v }))}
                  data-testid="pass-targhe"
                />
              </div>
              <div>
                <Label className="text-xs">Spese spedizione</Label>
                <CurrencyInput
                  value={form.spese_spedizione}
                  onChange={(v) => setForm((f) => ({ ...f, spese_spedizione: v }))}
                  data-testid="pass-spedizione"
                />
              </div>
            </div>

            <div className="text-right text-xs text-slate-600 mt-2">
              Totale ESENTE: <span className="font-mono font-semibold">{formatEUR(Number(form.ipt_importo || 0) + derived.cf_tot + Number(form.targhe || 0) + Number(form.spese_spedizione || 0))}</span>
            </div>
          </section>

          {/* ============ SEZIONE 4 (era 5) — Competenze agenzia ============ */}
          <section>
            <h3 className="text-lg font-bold border-b border-slate-300 pb-1 mb-2.5">Competenze agenzia (con IVA)</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Imponibile</Label>
                <CurrencyInput
                  value={form.competenze_agenzia_imponibile}
                  onChange={(v) => setForm((f) => ({ ...f, competenze_agenzia_imponibile: v }))}
                  data-testid="pass-comp-imp"
                />
              </div>
              <div>
                <Label className="text-xs">Codice IVA</Label>
                <CodiceIvaPicker
                  value={form.competenze_agenzia_iva_codice || ""}
                  onChange={(c) => setForm((f) => ({
                    ...f,
                    competenze_agenzia_iva_codice: c || null,
                    competenze_agenzia_iva_codice_override: true,
                  }))}
                  placeholder="Da agenzia..."
                  testId="pass-comp-iva-codice"
                />
                {form.competenze_agenzia_iva_codice_origin && (
                  <p className="text-[10px] text-slate-500 mt-1">
                    {form.competenze_agenzia_iva_codice_origin === "fornitore_default"
                      ? `Da: ${agenziaInfo?.name || "agenzia"}`
                      : `Default azienda${aziendaIvaDefault ? ` (${aziendaIvaDefault})` : ""}`}
                  </p>
                )}
              </div>
              <div className="text-right text-xs text-slate-600 pt-6">
                Totale IVATO:<br />
                <span className="font-mono font-semibold text-sm" data-testid="pass-comp-totale">{formatEUR(derived.ca_tot)}</span>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">
              IVA derivata da agenzia disbrigo selezionata. In assenza, default IVA azienda
              ({aziendaIvaDefault || "I22"}). Modificabile per casi specifici.
            </p>
          </section>

          {/* ============ SEZIONE 5 (era 6) — Ricarico ============ */}
          <section>
            <h3 className="text-lg font-bold border-b border-slate-300 pb-1 mb-2.5">Ricarico (con IVA)</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Imponibile</Label>
                <CurrencyInput
                  value={form.ricarico_imponibile}
                  onChange={(v) => setForm((f) => ({ ...f, ricarico_imponibile: v }))}
                  data-testid="pass-ric-imp"
                />
              </div>
              <div>
                <Label className="text-xs">Codice IVA</Label>
                <div className="flex items-center gap-2 h-9 px-3 bg-slate-100 border border-slate-200 rounded text-sm">
                  <span className="font-mono">{form.ricarico_iva_codice || "I22"}</span>
                  <span className="text-[10px] text-slate-400">🔒 fisso</span>
                </div>
              </div>
              <div className="text-right text-xs text-slate-600 pt-6">
                Totale IVATO:<br />
                <span className="font-mono font-semibold text-sm" data-testid="pass-ric-totale">{formatEUR(derived.ric_tot)}</span>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">
              IVA fissa (default azienda). Configurabile a livello master data.
            </p>
          </section>

          {/* ============ SEZIONE 6 (era 7) — Riepilogo + Prezzo cliente ============ */}
          <section>
            <h3 className="text-lg font-bold border-b border-slate-300 pb-1 mb-2.5">Riepilogo e prezzo cliente</h3>
            <div className="bg-slate-100 rounded-md p-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <div className="font-semibold text-amber-800 uppercase text-[10px] tracking-wide col-span-2 mb-0.5">Acquisto (esente IVA + IVATI)</div>
              <Row label="IPT" value={formatEUR(form.ipt_importo)} />
              <Row label="Costi fissi" value={formatEUR(derived.cf_tot)} />
              <Row label="Competenze ag. (IVA)" value={formatEUR(derived.ca_tot)} />
              <Row label="Targhe" value={formatEUR(form.targhe)} />
              <Row label="Spese spedizione" value={formatEUR(form.spese_spedizione)} />
              <Row label="Totale acquisto" value={formatEUR(derived.tot_acquisto)} bold testId="pass-tot-acquisto" />
            </div>

            {/* Sprint 4F2.2.1 F2: Prezzo suggerito — blu, NO grassetto, font più piccolo */}
            <div className="mt-4 flex items-baseline justify-between border-b border-blue-100 pb-2">
              <div className="text-sm uppercase tracking-wide text-blue-900 font-medium">Prezzo suggerito</div>
              <div className="text-base font-mono text-blue-700" data-testid="pass-prezzo-suggerito">{formatEUR(derived.sug)}</div>
            </div>
            <div className="text-[11px] text-slate-500 mb-3 mt-1">
              = Acquisto ({formatEUR(derived.tot_acquisto)}) + Ricarico IVATO ({formatEUR(derived.ric_tot)})
            </div>

            {/* Sprint 4F2.2.1 F1: Prezzo personalizzato — controlled input PURO, nessun bottone "Usa suggerito", nessun sync */}
            <div className="bg-cyan-50 border border-cyan-200 rounded-md p-3 mt-2">
              <Label className="text-xs uppercase tracking-wide text-cyan-900 font-semibold mb-1.5 block">
                Prezzo personalizzato <span className="text-rose-600">*</span>
              </Label>
              <CurrencyInput
                value={form.prezzo_vendita_cliente}
                onChange={(v) => setForm((f) => ({ ...f, prezzo_vendita_cliente: v }))}
                className="text-xl font-mono bg-white"
                data-testid="pass-prezzo-vendita-cliente"
              />
              <p className="text-[11px] text-cyan-800 mt-1">
                Prezzo che verrà mostrato al cliente nel preventivo/contratto (input libero, può differire dal suggerito).
              </p>
            </div>

            {/* Sprint 4F2.2 B8: margine compatto, base, emerald */}
            <div className={`mt-2 text-right text-base font-mono ${derived.margine >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="pass-margine">
              Margine passaggio: {derived.margine >= 0 ? "+" : ""}{formatCurrencyIT(derived.margine, false)} €
            </div>
          </section>

          {/* ============ SEZIONE 7 (era 8) — Note ============ */}
          <section>
            <h3 className="text-lg font-bold border-b border-slate-300 pb-1 mb-2.5">Note</h3>
            <Textarea
              value={form.note || ""}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              rows={2}
              placeholder="Note opzionali sul passaggio di proprietà"
              data-testid="pass-note"
            />
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pass-btn-annulla">Annulla</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="pass-btn-salva">
            {saving ? "Salvataggio..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, bold, testId }) {
  return (
    <>
      <div className={bold ? "font-semibold" : "text-slate-600"}>{label}</div>
      <div className={`font-mono text-right ${bold ? "font-bold" : ""}`} data-testid={testId}>{value}</div>
    </>
  );
}



// =============== Sprint 4I1.B3 — Popup Visualizzazione codici IVA (readonly) ===============
const IVA_LABEL_MAP = {
  "N1": "N1 — Esclusa art.15 (anticipazione)",
  "I22": "I22 — Imponibile 22%",
  "I10": "I10 — Imponibile 10%",
  "I4":  "I4 — Imponibile 4%",
  "E10": "E10 — Esente art.10",
  "N5":  "N5 — Non imponibile (regime margine)",
  "N4":  "N4 — Non imponibile art.7",
};

function ivaLabel(code) {
  if (!code) return <span className="text-slate-400 italic">non codificato</span>;
  return <span className="font-mono">{IVA_LABEL_MAP[code] || code}</span>;
}

function originLabel(origin, agenziaName) {
  if (!origin) return null;
  if (origin === "fornitore_default") return <span className="text-xs text-slate-500">(da {agenziaName || "agenzia"})</span>;
  if (origin === "default_azienda") return <span className="text-xs text-slate-500">(default azienda)</span>;
  if (origin === "override_utente") return <span className="text-xs text-amber-700">(override utente)</span>;
  return <span className="text-xs text-slate-500">({origin})</span>;
}

function PassaggioIvaInfoPopup({ open, onOpenChange, passaggio, agenziaName }) {
  if (!passaggio) return null;
  const p = passaggio;
  const flag = p?._data_quality?.iva_da_codificare;
  const modalita = p?.costi_fissi_modalita || "totale_unico";
  const det = p?.costi_fissi_dettaglio || {};
  const cf_tot = Number(p?.costi_fissi_totale_calc ?? p?.costi_fissi_totale ?? 0);
  const ca_imp = Number(p?.competenze_agenzia_imponibile || 0);
  const ca_iva_pct = Number(p?.competenze_agenzia_iva_percent_calc ?? 0);
  const ca_iva_calc = Number(p?.competenze_agenzia_iva_calc || 0);
  const ca_tot = Number(p?.competenze_agenzia_totale || (ca_imp + ca_iva_calc));
  const ric_imp = Number(p?.ricarico_imponibile || 0);
  const ric_iva_pct = Number(p?.ricarico_iva_percent_calc ?? 0);
  const ric_iva_calc = Number(p?.ricarico_iva_calc || 0);
  const ric_tot = Number(p?.ricarico_totale || (ric_imp + ric_iva_calc));
  const tot_acq = Number(p?.totale_acquisto_calcolato || 0);
  const prezzo_sug = Number(p?.prezzo_suggerito || 0);
  const pv_cli = Number(p?.prezzo_vendita_cliente || 0);
  const margine = Number(p?.margine_passaggio || 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dlg-passaggio-iva-info">
        <DialogHeader>
          <DialogTitle>Dettaglio Passaggio di proprietà</DialogTitle>
          <DialogDescription>
            Vista completa readonly. Per modificare clicca sull'icona matita.
          </DialogDescription>
        </DialogHeader>
        {flag && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3" data-testid="iva-da-codificare-banner">
            ⚠️ Codici IVA da rivedere (record migrato automaticamente). Sprint pulizia: 4I1.D.
          </div>
        )}
        <div className="space-y-4 text-sm">
          {/* Tipologia */}
          <section>
            <h4 className="font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2">Tipologia</h4>
            <div className="grid grid-cols-3 gap-2">
              <div><span className="text-xs text-slate-500">Tipo:</span> <span className="font-mono">{TIPI_LABEL[p.tipo] || p.tipo}</span></div>
              <div><span className="text-xs text-slate-500">Data atto:</span> <span className="font-mono">{formatDateIT(p.data_atto)}</span></div>
              <div><span className="text-xs text-slate-500">Agenzia:</span> {agenziaName || <span className="text-slate-400">—</span>}</div>
            </div>
          </section>
          {/* IPT */}
          <section>
            <h4 className="font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2">IPT</h4>
            <div className="flex justify-between">
              <span>Importo: <span className="font-mono">{formatEUR(p.ipt_importo)}</span></span>
              <span>{ivaLabel(p.ipt_iva_codice || "N1")} <span className="text-[10px] text-slate-400">🔒 fisso</span></span>
            </div>
          </section>
          {/* Costi fissi */}
          <section>
            <h4 className="font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2">
              Costi fissi <span className="text-xs font-normal text-slate-500">({modalita === "dettaglio" ? "dettaglio" : "totale unico"})</span>
            </h4>
            {modalita === "dettaglio" ? (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {[
                  ["emolumenti_aci", "Emolumenti ACI"],
                  ["imposta_bollo_pra", "Bollo PRA"],
                  ["diritti_dt", "Diritti DT"],
                  ["imposta_bollo_carta_circolazione", "Bollo carta circolazione"],
                  ["recupero_commissioni", "Recupero commissioni"],
                  ["costo_marca_bollo", "Marca bollo"],
                  ["costo_incasso_pra", "Incasso PRA"],
                ].map(([k, lab]) => (
                  <div key={k} className="flex justify-between">
                    <span className="text-slate-600">{lab}:</span>
                    <span className="font-mono">{formatEUR(det[k])}</span>
                  </div>
                ))}
              </div>
            ) : null}
            <div className="flex justify-between mt-2 pt-2 border-t border-slate-100">
              <span>Totale costi fissi: <span className="font-mono font-semibold">{formatEUR(cf_tot)}</span></span>
              <span>{ivaLabel(p.costi_fissi_iva_codice || "N1")} <span className="text-[10px] text-slate-400">🔒 fisso</span></span>
            </div>
          </section>
          {/* Competenze agenzia */}
          <section>
            <h4 className="font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2">Competenze agenzia</h4>
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div><span className="text-slate-500">Imponibile:</span><br /><span className="font-mono">{formatEUR(ca_imp)}</span></div>
              <div><span className="text-slate-500">IVA %:</span><br /><span className="font-mono">{ca_iva_pct}%</span></div>
              <div><span className="text-slate-500">IVA calcolata:</span><br /><span className="font-mono">{formatEUR(ca_iva_calc)}</span></div>
              <div><span className="text-slate-500">Totale IVATO:</span><br /><span className="font-mono font-semibold">{formatEUR(ca_tot)}</span></div>
            </div>
            <div className="text-xs mt-1.5">
              {ivaLabel(p.competenze_agenzia_iva_codice)}
              {originLabel(p.competenze_agenzia_iva_codice_origin, agenziaName)}
              {p.competenze_agenzia_iva_codice_override && <span className="ml-1 text-[10px] text-amber-700">override</span>}
            </div>
          </section>
          {/* Ricarico */}
          <section>
            <h4 className="font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2">Ricarico</h4>
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div><span className="text-slate-500">Imponibile:</span><br /><span className="font-mono">{formatEUR(ric_imp)}</span></div>
              <div><span className="text-slate-500">IVA %:</span><br /><span className="font-mono">{ric_iva_pct}%</span></div>
              <div><span className="text-slate-500">IVA calcolata:</span><br /><span className="font-mono">{formatEUR(ric_iva_calc)}</span></div>
              <div><span className="text-slate-500">Totale IVATO:</span><br /><span className="font-mono font-semibold">{formatEUR(ric_tot)}</span></div>
            </div>
            <div className="text-xs mt-1.5">
              {ivaLabel(p.ricarico_iva_codice || "I22")} <span className="text-[10px] text-slate-400">🔒 fisso (default azienda)</span>
            </div>
          </section>
          {/* Targhe */}
          <section>
            <h4 className="font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2">Targhe</h4>
            <div className="flex justify-between">
              <span>Importo: <span className="font-mono">{formatEUR(p.targhe)}</span></span>
              <span>{ivaLabel(p.targhe_iva_codice || "N1")} <span className="text-[10px] text-slate-400">🔒 fisso</span></span>
            </div>
          </section>
          {/* Spese spedizione (no IVA) */}
          <section>
            <h4 className="font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2">Spese spedizione</h4>
            <div className="flex justify-between">
              <span>Importo: <span className="font-mono">{formatEUR(p.spese_spedizione)}</span></span>
              <span className="text-xs text-slate-500">Escluso da fatturazione IVA</span>
            </div>
          </section>
          {/* Riepilogo */}
          <section className="bg-slate-50 rounded p-3">
            <h4 className="font-semibold text-slate-800 border-b border-slate-300 pb-1 mb-2">Riepilogo prezzi</h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
              <div className="flex justify-between"><span>Totale acquisto:</span><span className="font-mono font-semibold">{formatEUR(tot_acq)}</span></div>
              <div className="flex justify-between"><span>Ricarico IVATO:</span><span className="font-mono">{formatEUR(ric_tot)}</span></div>
              <div className="flex justify-between text-blue-700"><span>Prezzo suggerito:</span><span className="font-mono font-semibold">{formatEUR(prezzo_sug)}</span></div>
              <div className="flex justify-between"><span>Prezzo personalizzato:</span><span className="font-mono">{formatEUR(pv_cli)}</span></div>
              <div className="flex justify-between col-span-2 pt-1 border-t border-slate-200">
                <span>Margine:</span>
                <span className={`font-mono font-semibold ${margine >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatEUR(margine)}</span>
              </div>
            </div>
          </section>
          {p.note && (
            <section>
              <h4 className="font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2">Note</h4>
              <p className="text-xs text-slate-700">{p.note}</p>
            </section>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// =============== Sprint 4I1.B3 — Sezione Avanzato codici IVA (modal modifica) ===============
function AvanzatoIvaSection({ form, setForm }) {
  const [open, setOpen] = useState(false);
  const fixedRows = [
    { key: "ipt_iva_codice", label: "IPT", expected: "N1" },
    { key: "costi_fissi_iva_codice", label: "Costi fissi", expected: "N1" },
    { key: "targhe_iva_codice", label: "Targhe", expected: "N1" },
    { key: "ricarico_iva_codice", label: "Ricarico", expected: "I22" },
  ];
  return (
    <section data-testid="pass-avanzato-section">
      <button type="button"
              onClick={() => setOpen((s) => !s)}
              className="w-full text-left flex items-center gap-2 text-base font-bold border-b border-slate-300 pb-1 mb-2.5"
              data-testid="pass-avanzato-toggle">
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Avanzato — Codici IVA
      </button>
      {open && (
        <div className="space-y-3 text-sm">
          <p className="text-[11px] text-slate-500">
            IPT/Costi fissi/Targhe/Ricarico hanno codici IVA fissi per normativa (locked). Competenze e
            spese spedizione seguono il fornitore agenzia disbrigo selezionato. Modifica solo per casi specifici.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {fixedRows.map((r) => (
              <div key={r.key}>
                <Label className="text-xs">{r.label}</Label>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{form[r.key] || r.expected}</span>
                  <span className="text-[10px] text-slate-400">🔒 fisso</span>
                </div>
              </div>
            ))}
            <div>
              <Label className="text-xs">Competenze agenzia</Label>
              <CodiceIvaPicker
                value={form.competenze_agenzia_iva_codice || ""}
                onChange={(c) => setForm((f) => ({
                  ...f,
                  competenze_agenzia_iva_codice: c || null,
                  competenze_agenzia_iva_codice_override: true,
                }))}
                placeholder="Da agenzia..."
                testId="pass-iva-competenze"
              />
              {form.competenze_agenzia_iva_codice_origin && (
                <p className="text-[10px] text-slate-500 mt-1">
                  Default: {form.competenze_agenzia_iva_codice_origin === "fornitore_default"
                    ? "da agenzia" : "azienda"}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">Spese spedizione</Label>
              <CodiceIvaPicker
                value={form.spese_spedizione_iva_codice || ""}
                onChange={(c) => setForm((f) => ({ ...f, spese_spedizione_iva_codice: c || null }))}
                placeholder="Da agenzia..."
                testId="pass-iva-spese-spedizione"
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
